import { useEffect, useRef, useState, useMemo } from "react";
import {
	Box,
	IconButton,
	ListItemIcon,
	MenuItem,
	Tooltip,
	useTheme,
} from "@mui/material";
import {
	AddCircleOutline,
	AssignmentReturn,
	AttachMoney,
	Delete,
	DeleteOutline,
	Edit,
	Refresh,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MRT_ColumnDef } from "material-react-table";
import { toast } from "react-toastify";

import TableView from "../../components/TableView";
import ConfirmDialog from "../../components/ConfirmDialog";
import SupplierPaymentModal from "../payments/SupplierPaymentModal";
import SupplyReport from "./SupplyReport";
import ReturnSupplyModal from "./ReturnSupplyModal";
import SupplyModal from "./SupplyModal";
import { SupplyProvider } from "./SupplyContext";
import { canDeleteSupply, canEditSupply, getSupplyRowSx, isSupplyOverdue } from "./supplyHelpers";
import { useAuth } from "../../hooks/useAuth";
import { useData } from "../../hooks/useData";
import { useUI } from "../../providers/MoleUIContext";
import { useDateRange, periodRequestConfig } from "../../hooks/useDateRange";
import { allowOrWarn, Util } from '../../util/Util';
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import { createSignCell } from "../../util/cellFormatters";
import { useAppSocket } from "../../providers/AppSocketContext";
import { parseISODay } from "../../util/businessClock";

import { roundQty } from "../../util/quantity";
import { canAmend, canDrop, canRecord } from '../../util/access';
interface Props {
	bundle?: any;
	hideColumns?: string[];
	onCallback?: () => void;
}

const SupplySituation = ({ bundle, hideColumns, onCallback }: Props) => {
	const theme = useTheme();
	const [toPay, setToPay] = useState<any>(null);
	const [toView, setToView] = useState<any>(null);
	const [toEdit, setToEdit] = useState<any>(null);
	const [toReturn, setToReturn] = useState<any>(null);
	const [toDelete, setToDelete] = useState<any>(null);
	const [toDeleteBulk, setToDeleteBulk] = useState<any[]>([]);
	const [newRecord, setNewRecord] = useState(false);

	const { api, session, access } = useAuth();
	const { appSettings } = useAppSocket();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(bundle ? undefined : LocalStorageKeys.SUPPLY_SIT_BATCHED_RECORDS);
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading, setData } =
		useData(LocalStorageKeys.SUPPLY_SIT_BATCHED_RECORDS);

	const paymentMade = useRef(false);
	const toastIdRef = useRef<string | number>(-1);
	const tableRef = useRef<any>(null);
	const firstFetchRef = useRef(false);

	const supplierName = bundle?.name || bundle?.supplier;
	const isEmbedded = Boolean(bundle);

	useEffect(() => {
		return () => {
			if (paymentMade.current) onCallback?.();
		};
	}, [onCallback]);

	useEffect(() => {
		if (!isEmbedded && !loadFromIndexedDB() && period && !firstFetchRef.current) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, [period, isEmbedded]);

	useEffect(() => {
		if (isEmbedded) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, [bundle, isEmbedded]);

	const fetchData = async (useLoadModal: boolean = true) => {
		if (isEmbedded && !supplierName) return;
		setLoading(true);

		const path = isEmbedded
			? `/supplies/situations/suppliers/${supplierName}/`
			: "/supplies/situations";

		try {
			const res = await api.get(path, {
				...(isEmbedded ? {} : periodRequestConfig(period)),
			});

			if (res?.status === 200) {
				if (isEmbedded) {
					setData(res.data);
				} else {
					saveFromServer(res.data, period);
				}
				toast.update(toastIdRef.current, {
					render: t("common:toast.success.refresh"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} else if (res?.status === 401 || res?.status === 422) {
				queueReRun(() => fetchData(useLoadModal));
			}
		} finally {
			setLoading(false);
		}
	};

	const handlePaymentSuccess = () => {
		paymentMade.current = true;
		setToPay(null);
		setNewRecord(false);
		toastIdRef.current = toast.loading(
			`${t("common:toast.success.changes")} ${t("common:toast.loading.refresh")}`,
		);
		fetchData(false);
	};

	const handleEditSuccess = () => {
		toastIdRef.current = toast.loading(
			`${t("common:toast.success.changes")} ${t("common:toast.loading.refresh")}`,
		);
		fetchData(false);
	};

	const handleDelete = async () => {
		if (!toDelete) return;
		setLoadingMessage("DELETE");
		const res = await api.delete(`/supplies/${toDelete.batch}`);
		if (res?.status === 204) {
			toastIdRef.current = toast.loading(
				`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`,
			);
			fetchData(false);
		} else if (res?.status === 409) {
			toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(handleDelete);
		}
		setLoadingMessage(null);
		setToDelete(null);
	};

	const handleBulkDelete = async () => {
		if (toDeleteBulk.length === 0) return;
		const payload = { batches: toDeleteBulk };

		setLoadingMessage("DELETE");

		const res = await api.post("/supplies/delete", payload);

		if (res?.status === 204) {
			toastIdRef.current = toast.loading(
				`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`,
			);
			tableRef.current?.resetRowSelection();
			fetchData(false);
		} else if (res?.status === 409) {
			toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(handleBulkDelete);
		}

		setLoadingMessage(null);
		setToDeleteBulk([]);
	};

	const canPay =
		canRecord(access, 'supply_payment')
		|| canRecord(access, 'payment')
		|| canRecord(access, 'supply');

	const canReturn = canRecord(access, 'supply_return');

	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.date"),
			id: "date",
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), "medium"),
			filterVariant: "date-range",
		},
		...(!isEmbedded
			? [{
				header: t("common:tables.supplier"),
				accessorKey: "supplier",
				filterVariant: "autocomplete" as const,
				filterFn: "contains" as const,
				columnFilterModeOptions: TEXT_FILTER_OPTIONS,
			}]
			: []),
		{
			header: t("common:tables.type"),
			accessorKey: "sup_type",
			filterVariant: "autocomplete",
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.amount"),
			accessorFn: (row: any) => Number(row.amount),
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: "contains",
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: "sum",
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{" "}
					{table.getColumn(cell.row.groupingColumnId ?? "").columnDef.header}:{" "}
					<Box sx={{ color: "info.main", display: "inline", fontWeight: "bold" }}>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			Footer: ({ table }) => {
				const [total, setTotal] = useState(0);

				useEffect(() => {
					const rows = table.getFilteredRowModel().rows;
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.amount);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
					}, 0);
					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: "bold", color: "success.main" }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.paidRefunded"),
			accessorFn: (row: any) => Number(row.paid),
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: "contains",
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: "sum",
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{" "}
					{table.getColumn(cell.row.groupingColumnId ?? "").columnDef.header}:{" "}
					<Box sx={{ color: "info.main", display: "inline", fontWeight: "bold" }}>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			Footer: ({ table }) => {
				const [total, setTotal] = useState(0);

				useEffect(() => {
					const rows = table.getFilteredRowModel().rows;
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.paid);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
					}, 0);
					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: "bold", color: "success.main" }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.owing"),
			accessorFn: (row: any) => Number(row.balance),
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: "contains",
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: "sum",
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{" "}
					{table.getColumn(cell.row.groupingColumnId ?? "").columnDef.header}:{" "}
					<Box sx={{ color: "info.main", display: "inline", fontWeight: "bold" }}>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			Footer: ({ table }) => {
				const [total, setTotal] = useState(0);

				useEffect(() => {
					const rows = table.getFilteredRowModel().rows;
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.balance);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
					}, 0);
					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: "bold", color: "success.main" }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:supplySituation.reference"),
			accessorKey: "reference",
			Cell: ({ cell }: { cell: any }) => (
				<button
					type="button"
					style={{
						background: "none",
						border: "none",
						padding: 0,
						color: "inherit",
						cursor: "pointer",
						textDecoration: "underline",
					}}
					onClick={() => setToView(cell.row.original)}
				>
					{cell.getValue()}
				</button>
			),
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:supplyDetail.dueDate"),
			id: "due_date",
			accessorFn: (row: any) => (row.due_date ? new Date(row.due_date) : null),
			Cell: ({ row }: { row: any }) => {
				const dueDate = row.original.due_date;
				if (!dueDate) return "—";
				const overdue = isSupplyOverdue(row.original);
				return (
					<Box
						component="span"
						sx={{
							color: overdue ? "error.main" : undefined,
							fontWeight: overdue ? 700 : undefined,
						}}
					>
						{Util.formatDate(dueDate, "medium")}
						{overdue ? ` (${t("common:status.overdue")})` : ""}
					</Box>
				);
			},
			filterVariant: "date-range",
		},
		{
			header: t("common:tables.user"),
			accessorKey: "user",
			filterVariant: "autocomplete",
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
	], [isEmbedded, t]);

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
					stateKey={bundle ? undefined : LocalStorageKeys.SUPPLY_SIT_BATCHED_RECORDS}
					header={isEmbedded ? t("common:supplySituation.title") : t("nav:tabs.supply_situation")}
					data={data}
					columns={columns}
					totals={[
						{ key: "amount", label: t("common:tables.amount") },
						{ key: "paid", label: t("common:tables.paidRefunded") },
						{ key: "balance", label: t("common:tables.owing") },
					]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={isEmbedded ? undefined : setPeriod}
					isLoading={loading}
					bundle={bundle}
					hideColumns={hideColumns}
					getRowSx={(row) => getSupplyRowSx(row.original, theme)}
					renderTopToolbarActions={(table) => (
						<Box sx={{ display: "flex", gap: "0.5rem" }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							<Tooltip title={t("common:ui.tooltips.newPayment")}>
								<IconButton
									onClick={() => canPay && setNewRecord(true)}
									color="success"
								>
									<AddCircleOutline />
								</IconButton>
							</Tooltip>
							{table.getSelectedRowModel().flatRows.length === 1
								&& canEditSupply(table.getSelectedRowModel().flatRows[0].original) && (
								<Tooltip title={t("common:ui.tooltips.editSelectedSupply")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canAmend(access, 'supply'))) {
												setToEdit(table.getSelectedRowModel().flatRows[0].original);
											}
										}}
										color="primary"
									>
										<Edit />
									</IconButton>
								</Tooltip>
							)}
							{table.getSelectedRowModel().flatRows.length === 1
								&& Util.compareStrings(
									table.getSelectedRowModel().flatRows[0].original.sup_type || "Supply",
									"Supply",
								)
								&& canReturn && (
								<Tooltip title={t("common:ui.tooltips.returnSelectedSupply")}>
									<IconButton
										color="warning"
										onClick={() => setToReturn(table.getSelectedRowModel().flatRows[0].original)}
									>
										<AssignmentReturn />
									</IconButton>
								</Tooltip>
							)}
							{table.getSelectedRowModel().flatRows.length > 0
								&& table.getSelectedRowModel().flatRows.every((row) =>
									canDeleteSupply(row.original),
								) && (
								<Tooltip title={t("common:ui.tooltips.deleteSelection")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canDrop(access, 'supply', 'delete_bulk'))) {
												const rowsBatches = [
													...new Set(
														table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch),
													),
												];
												if (rowsBatches.length === 1) {
													setToDelete(table.getSelectedRowModel().flatRows[0].original);
												} else {
													tableRef.current = table;
													setToDeleteBulk(rowsBatches);
												}
											}
										}}
										color="error"
									>
										<Delete />
									</IconButton>
								</Tooltip>
							)}
						</Box>
					)}
					renderRowActionMenuItems={(row, closeMenu) => [
						!(toDelete || toEdit || toReturn) && Number(row.original.balance) !== 0 ? (
							<MenuItem
								key="pay"
								onClick={() => {
									const isReturn = Util.compareStrings(row.original.sup_type, "Return");
									if (
										(isReturn && canRecord(access, 'supply_payment'))
										|| (!isReturn && canPay)
									) {
										setToPay({
											...row.original,
											supplier: row.original.supplier || supplierName,
										});
									}
									closeMenu();
								}}
								sx={{ color: "success.main" }}
							>
								<ListItemIcon sx={{ color: "success.main" }}>
									<AttachMoney />
								</ListItemIcon>
								{Util.compareStrings(row.original.sup_type, "Return")
									? t("common:ui.buttons.refund")
									: t("common:ui.buttons.pay")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn) && canEditSupply(row.original) ? (
							<MenuItem
								key="edit"
								onClick={() => {
									if (allowOrWarn(canAmend(access, 'supply'))) {
										setToEdit(row.original);
									}
									closeMenu();
								}}
								sx={{ color: "primary.main" }}
							>
								<ListItemIcon sx={{ color: "primary.main" }}>
									<Edit />
								</ListItemIcon>
								{t("common:ui.buttons.edit")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn)
							&& Util.compareStrings(row.original.sup_type || "Supply", "Supply")
							&& canReturn ? (
							<MenuItem
								key="return"
								onClick={() => {
									setToReturn(row.original);
									closeMenu();
								}}
								sx={{ color: "warning.main" }}
							>
								<ListItemIcon sx={{ color: "warning.main" }}>
									<AssignmentReturn />
								</ListItemIcon>
								{t("common:ui.buttons.return")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit || toReturn) && canDeleteSupply(row.original) ? (
							<MenuItem
								key="delete"
								onClick={() => {
									if (allowOrWarn(canDrop(access, 'supply'))) {
										setToDelete(row.original);
									}
									closeMenu();
								}}
								sx={{ color: "error.main" }}
							>
								<ListItemIcon sx={{ color: "error.main" }}>
									<Delete />
								</ListItemIcon>
								{t("common:ui.buttons.delete")}
							</MenuItem>
						) : null,
					]}
				/>
			</LocalizationProvider>

			{(newRecord || !!toPay) && (
				<SupplierPaymentModal
					isOpen={newRecord || !!toPay}
					toggle={() => {
						setNewRecord(false);
						setToPay(null);
					}}
					bundle={
						toPay || {
							supplier: supplierName,
						}
					}
					onSuccess={handlePaymentSuccess}
				/>
			)}

			{toView && (
				<SupplyReport
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={{ batch: toView.batch }}
				/>
			)}

			{!!toEdit && (
				<SupplyProvider>
					<SupplyModal
						isOpen={!!toEdit}
						toggle={() => setToEdit(null)}
						bundle={toEdit}
						onSuccess={handleEditSuccess}
					/>
				</SupplyProvider>
			)}

			{toReturn && (
				<ReturnSupplyModal
					isOpen={!!toReturn}
					toggle={() => setToReturn(null)}
					bundle={toReturn}
					onSuccess={() => {
						toastIdRef.current = toast.loading(
							`${t("common:toast.success.changes")} ${t("common:toast.loading.refresh")}`,
						);
						fetchData(false);
					}}
				/>
			)}

			<ConfirmDialog
				onConfirm={toDelete ? handleDelete : handleBulkDelete}
				onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]); }}
				isOpen={!!toDelete || toDeleteBulk.length > 0}
				setIsOpen={() => { }}
				title={<><DeleteOutline sx={{ color: "red" }} /> {t("common:dialog.deleteConfirmation")}</>}
				message={
					toDelete ?
						<>
							{t("common:supply.confirm.deleteSingleStart")}
							<b><u>{toDelete?.supplier || supplierName}</u></b>&nbsp;
							{t("common:supply.confirm.deleteSingleValue")}&nbsp;
							<b><u>{Util.formatCurrency(toDelete?.amount, appSettings?.BIZ_CURRENCY?.value || "USD")}</u></b>&nbsp;
							{t("common:supply.confirm.deleteSingleDate")} <b><u>{Util.formatDate(toDelete?.date, "full")}</u></b>
							, {t("common:tables.reference").toLowerCase()} <button
								type="button"
								style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
								onClick={() => setToView(toDelete)}
							>
								<b><u>{toDelete?.reference}</u></b>
							</button>.
						</>
						:
						<>
							{t("common:supply.confirm.deleteBulk", { count: Number(toDeleteBulk.length) })}
						</>
				}
				confirmBtnColor="error"
				confirmBtnVariant="contained"
				confirmBtnText={<>{t("common:ui.buttons.delete")}</>}
			/>
		</>
	);
};

export default SupplySituation;